"use client";

import { useState } from "react";
import NiceModal from "@ebay/nice-modal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ReactQueryStreamedHydration } from "@tanstack/react-query-next-experimental";
import { httpBatchLink, loggerLink } from "@trpc/client";
import { Provider as JotaiProvider } from "jotai";
import superjson from "superjson";

import { ThemeProvider } from "~/components/common/ThemeProvider";
import { ToastProvider, TooltipProvider } from "~/components/ui";
import { SwapKitProvider } from "~/context/swapkit";
import { env } from "~/env.mjs";
import { api } from "~/services/api";

const getBaseUrl = () => {
  // TODO: move base URL transform into env.mjs

  if (typeof window !== "undefined") return ""; // browser should use relative url
  if (process.env.NODE_ENV === "production") return "https://forge.coinbot.app"; // SSR should use deployment URL

  return `http://localhost:${env.PORT}`; // dev SSR should use localhost
};

export function AppProviders({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 5 * 1000 } } }),
  );

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (opts) =>
            process.env.NODE_ENV === "development" ||
            (opts.direction === "down" && opts.result instanceof Error),
        }),
        httpBatchLink({
          transformer: superjson,
          url: `${getBaseUrl()}/api/trpc`,
          headers() {
            const headers = new Headers();

            headers.set("x-trpc-source", "nextjs-api");

            return headers;
          },
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryStreamedHydration transformer={superjson}>
          <JotaiProvider>
            <SwapKitProvider>
              <ThemeProvider attribute="class" defaultTheme="dark">
                <NiceModal.Provider>
                  <TooltipProvider>
                    <ToastProvider>{children}</ToastProvider>
                  </TooltipProvider>
                </NiceModal.Provider>
              </ThemeProvider>
            </SwapKitProvider>
          </JotaiProvider>
        </ReactQueryStreamedHydration>

        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </api.Provider>
  );
}
